/**
 * ComposingSafeInput —— 组合输入（输入法拼音/五笔）安全的受控 Input。
 *
 * 【问题】受控 Input 的 value 来自上层数据。用户用输入法组合期间（如拼音 nihao 尚未上屏），
 * DOM 值（nihao）≠ 受控值（还没回流，仍是旧值），此时**只要发生任意一次上层重渲染**，
 * React 就会在 commit 阶段把 DOM 值强写回受控值 —— 表现为组合被打断、拼音字母直接上屏。
 *
 * React 的组合保护只覆盖 onChange 事件分发路径；commit 阶段的受控值同步没有保护。
 * 运行时实测（表格「主平台发布账号」列）：每次 compositionupdate 后 DOM 均被强写回旧值；
 * 对照组（全局搜索框、TextAreaPopover 弹层）同样流程 0 次强写、组合存活。
 *
 * 表格单元格为何比折叠面板更易触发：折叠面板走 bedrock Form 字段管理（rc-field-form，
 * 内建组合保护），且编辑态不回流外部；表格单元格是裸受控 Input + 每次输入都回流上层数据
 * （属性行 property_values → 表格 value → 表单 cooperate_accounts），重渲染必然发生。
 *
 * 【做法】组合期间把受控值冻结为「本地输入值」（始终等于当前 DOM 值），使 commit 阶段
 * 二者相等而不写 DOM，组合得以延续；组合结束后再交还外部受控。与 rc-field-form 行为对齐，
 * 保证同一 schema 在折叠/表格两种模式下输入体验一致。
 */
import React, { useEffect, useRef, useState } from 'react';
import { Input } from '@bedrock/components';

type ComposingSafeInputProps = React.ComponentProps<typeof Input>;

export const ComposingSafeInput: React.FC<ComposingSafeInputProps> = ({
  value,
  onChange,
  onCompositionStart,
  onCompositionEnd,
  ...rest
}) => {
  /** 是否处于输入法组合中：组合期间不跟随外部 value，避免强写 DOM 打断组合 */
  const composingRef = useRef(false);
  const [localValue, setLocalValue] = useState<string>(() => (value as string) ?? '');

  // 外部值变化 → 同步为受控值；组合期间跳过（此时 DOM 才是用户真正在编辑的内容）
  useEffect(() => {
    if (!composingRef.current) {
      setLocalValue((value as string) ?? '');
    }
  }, [value]);

  return (
    <Input
      {...rest}
      value={localValue}
      onChange={(e) => {
        // 本地值始终与 DOM 保持一致（commit 阶段不产生 DOM 写入，组合得以延续），
        // 同时照常外发，保证上层数据实时同步（校验 / 字数统计等依赖实时值的逻辑不受影响）
        setLocalValue(e.target.value);
        onChange?.(e);
      }}
      onCompositionStart={(e) => {
        composingRef.current = true;
        onCompositionStart?.(e);
      }}
      onCompositionEnd={(e) => {
        composingRef.current = false;
        // 以 DOM 终值为准，随后由外部受控接管（组合结束的最终值已由 onChange 外发）
        setLocalValue(e.currentTarget.value);
        onCompositionEnd?.(e);
      }}
    />
  );
};
